import { View } from "@-/com.npmjs.react-native";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { useCSSVariable } from "@-/com.npmjs.uniwind";
import {
  SlideGlyph,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.slide.index._components.mdx.slide-table";
import {
  type SlideHanziScriptTableGate,
  useSlideHanziScriptTableGate,
} from "#use-slide-hanzi-script-table-gate";

/**
 * The "Choose Your Script" comparison table for the onboarding slide
 * (`slides/000/002-hanzi-script.mdx`). Authored as a bare `<HanziScriptTable />`
 * alongside `<HanziScriptToggle />` — the two are split so a slide can place the
 * explanatory table independently of the live toggle control.
 *
 * It lists a few example characters whose Simplified and Traditional forms
 * diverge, laid out as simplified · meaning · traditional so the difference is
 * concrete, and tints the column matching the user's current script (sourced
 * from {@link AppLayoutContext}, the same field the toggle persists) so the
 * selection made above is reflected here. The shared pinyin sits to the right of
 * the glyph in *both* script columns — repeating it on each side emphasizes that
 * the two forms are pronounced identically.
 */

// Example characters whose Simplified and Traditional forms differ noticeably,
// so the contrast the slide is teaching is visible at a glance. `pinyin` is the
// single pronunciation both forms share — shown under each glyph in both columns.
const scriptExamples = [
  { meaning: "love", pinyin: "ài", simplified: "爱", traditional: "愛" },
  { meaning: "country", pinyin: "guó", simplified: "国", traditional: "國" },
  { meaning: "to learn", pinyin: "xué", simplified: "学", traditional: "學" },
  { meaning: "dragon", pinyin: "lóng", simplified: "龙", traditional: "龍" },
] as const;

/** Theme colors from `useCSSVariable` may be HSL channels or resolved hex/rgb/hsl strings. */
function hslFromChannels(channels: string | number | undefined): string | undefined {
  if (channels === undefined || channels === "") {
    return undefined;
  }

  const value = String(channels);
  if (value.startsWith("hsl") || value.startsWith("#") || value.startsWith("rgb")) {
    return value;
  }

  return `hsl(${value})`;
}

// Glyph with its (shared) pinyin to the right. Rendered as a `View` row so the
// glyph and pinyin sit side by side with their own metrics (a `\n` inside the
// glyph `Text` silently dropped the pinyin run on native).
function GlyphWithPinyin({ glyph, pinyin }: { glyph: string; pinyin: string }) {
  return (
    <View className="flex-row items-center gap-1.5">
      <SlideGlyph>{glyph}</SlideGlyph>
      <Text className="text-sm text-muted-foreground" selectable>
        {pinyin}
      </Text>
    </View>
  );
}

/**
 * What slide MDX renders for `<HanziScriptTable />`. Authored MDX has no JSX parent that could
 * call a gate, so this entry in the slide's MDX component map is that parent.
 */
export function SlideHanziScriptTableSlot() {
  const gate = useSlideHanziScriptTableGate();
  return <>{gate !== null && <SlideHanziScriptTable gate={gate} />}</>;
}

export function SlideHanziScriptTable({ gate }: { gate: SlideHanziScriptTableGate }) {
  const { script } = gate;

  // Subtle accent tint for the selected column, resolved from the theme so it
  // reads correctly on both the light and dark table grids.
  const accent = hslFromChannels(useCSSVariable("--color-accent"));
  const highlightStyle = accent == null ? undefined : { backgroundColor: accent };
  const simplifiedHighlight = script === "simplified" ? highlightStyle : undefined;
  const traditionalHighlight = script === "traditional" ? highlightStyle : undefined;

  return (
    <View className="flex-col">
      <Table>
        <THead>
          <TR>
            <TH style={simplifiedHighlight}>Simplified</TH>
            <TH>Meaning</TH>
            <TH style={traditionalHighlight}>Traditional</TH>
          </TR>
        </THead>
        <TBody>
          {scriptExamples.map((example) => (
            <TR key={example.simplified}>
              <TD style={simplifiedHighlight}>
                <GlyphWithPinyin glyph={example.simplified} pinyin={example.pinyin} />
              </TD>
              <TD>{example.meaning}</TD>
              <TD style={traditionalHighlight}>
                <GlyphWithPinyin glyph={example.traditional} pinyin={example.pinyin} />
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </View>
  );
}
